// Access-request inbox: a header badge + popover so an owner/admin can DISCOVER
// and act on incoming access requests from anywhere, instead of reopening each
// session's drawer. Backed by GET /api/access-requests/inbox (owner-scoped — the
// viewer's own sessions, admins included). Each row: "<requester> wants
// <session>" + Grant / Deny / View.
//
// Styled with the existing header-popover idiom (.meta-info-pop tokens) so it
// reads as native chrome. The count refreshes on natural triggers — boot, after a
// decide here, and on the `inbox:refresh` event the drawer's Sharing panel fires
// when it grants/denies. No live push, and closing the drawer does NOT refresh
// (only an actual decide changes the count).
import { $, esc, get, postStatus } from './core'
import { openDetail } from './sessions'

// Last-fetched pending requests, so the popover paints without a refetch and the
// badge and panel stay in sync after an in-place grant/deny.
var pending: Array<{ id: string; sessionId: string; sessionTitle: string; requesterEmail: string }> = [];
// The TRUE pending count, which can exceed `pending.length` when the server caps
// the list at INBOX_LIMIT. The badge shows this (as "N+" when capped) so it never
// under-reports — or vanishes while capped-out requests still wait.
var total = 0;

// Other surfaces that decide a request (the drawer's Sharing panel) fire this
// event so the badge/count stays in sync without a module import cycle.
document.addEventListener('inbox:refresh', function () { refreshInbox(); });

// Fetch the inbox and repaint the badge. Returns the promise so boot can sequence.
// Safe to call anytime; on error it just leaves the last known state.
// No latest-wins guard: overlapping fetches just clobber `pending`/`total` in
// completion order, which is self-correcting since every write is full server
// truth (no local mutation between fetches to lose). The last response wins and is
// authoritative — a stale one only means a slightly-late but still-consistent count.
export function refreshInbox() {
  return get('/api/access-requests/inbox')
    .then(function (d) {
      pending = (d && d.requests) || [];
      // Fall back to the row count if an older server omits `total`.
      total = d && typeof d.total === 'number' ? d.total : pending.length;
      render();
    })
    .catch(function () { /* transient — keep the last count rather than fl/blank */ });
}

// One repaint from `pending`: the badge count (in place) plus, if the popover is
// open, its list — so the two views can never drift. Every mutation calls this.
function render() {
  paintBadge();
  var pop = document.getElementById('inboxPop');
  if (pop && pop.classList.contains('on')) paintPop(pop);
}

// The header badge: a "Requests" button with a count, hidden entirely when zero
// (nothing to act on → no chrome). Updates the count IN PLACE and never rebuilds
// #inboxPop — so a refresh (or acting on a request) can't collapse an open popover
// or orphan the outside-click listener. Clicking toggles the popover.
function paintBadge() {
  var slot = $('#inbox');
  if (!slot) return;
  // Hide the chrome only when NOTHING is pending. Gate on `total`, not the capped
  // list length, so the badge doesn't vanish while >INBOX_LIMIT requests wait.
  if (!total) { closePop(); slot.innerHTML = ''; return; }
  // "N+" when the server capped the list below the true total (more than shown).
  var label = total > pending.length ? pending.length + '+' : String(total);
  var wrap = slot.querySelector('.inbox-wrap');
  if (!wrap) {
    // First paint (or after a zero → non-zero transition): build the chrome once.
    slot.innerHTML =
      '<span class="inbox-wrap">' +
        '<button type="button" class="inbox-btn" id="inboxBtn" title="Access requests">' +
          'Requests <span class="inbox-count">' + label + '</span></button>' +
        '<div class="inbox-pop" id="inboxPop"></div>' +
      '</span>';
    var btn = document.getElementById('inboxBtn');
    if (btn) btn.onclick = function (e) { e.stopPropagation(); togglePop(); };
    return;
  }
  // Already built: just update the count; leave #inboxPop (possibly open) intact.
  var cnt = slot.querySelector('.inbox-count');
  if (cnt) cnt.textContent = label;
}

// TODO(a11y, deferred): the popover has no keyboard/AT affordances yet — no
// Escape-to-close, no aria-expanded/aria-haspopup on the button, the count isn't
// aria-live (a screen reader never hears it change), outside-close is mousedown-
// only (tabbing away leaves it open), and deciding a request removes the focused
// button, dropping focus to <body>. Acceptable for V0; revisit for accessibility.
function togglePop() {
  var pop = document.getElementById('inboxPop');
  if (!pop) return;
  if (pop.classList.contains('on')) { closePop(); return; }
  paintPop(pop);
  pop.classList.add('on');
  // Dismiss on outside-click (same pattern as the repo multiselect / meta-info-pop).
  setTimeout(function () { document.addEventListener('mousedown', onDocClick); }, 0);
}

function onDocClick(e: MouseEvent) {
  var wrap = document.querySelector('.inbox-wrap');
  if (wrap && !wrap.contains(e.target as Node)) closePop();
}

function closePop() {
  var pop = document.getElementById('inboxPop');
  if (pop) pop.classList.remove('on');
  document.removeEventListener('mousedown', onDocClick);
}

// Render the popover body from `pending`. Each row: who wants what + Grant / Deny
// / View session.
function paintPop(pop: HTMLElement) {
  if (!pending.length) { pop.innerHTML = '<div class="inbox-empty">No pending requests.</div>'; closePop(); return; }
  pop.innerHTML =
    '<div class="inbox-head">Access requests</div>' +
    pending.map(function (r) {
      return '<div class="inbox-row" data-req="' + esc(r.id) + '" data-session="' + esc(r.sessionId) + '">' +
        '<div class="inbox-ask"><span class="inbox-who">' + esc(r.requesterEmail) + '</span>' +
          ' wants <span class="inbox-sess">' + esc(r.sessionTitle) + '</span></div>' +
        '<div class="inbox-acts">' +
          '<button type="button" class="inbox-grant">Grant</button>' +
          '<button type="button" class="inbox-deny">Deny</button>' +
          '<button type="button" class="inbox-view">View session</button>' +
        '</div></div>';
    }).join('') +
    '<div class="inbox-err" id="inboxErr"></div>';

  Array.prototype.forEach.call(pop.querySelectorAll('.inbox-row'), function (row: HTMLElement) {
    var reqId = row.getAttribute('data-req')!;
    var sessionId = row.getAttribute('data-session')!;
    var acts = row.querySelector('.inbox-acts') as HTMLElement;
    (row.querySelector('.inbox-grant') as HTMLElement).onclick = function () { decide(reqId, true, acts); };
    (row.querySelector('.inbox-deny') as HTMLElement).onclick = function () { decide(reqId, false, acts); };
    (row.querySelector('.inbox-view') as HTMLElement).onclick = function () {
      // openDetail pushes ?session=<id> as a history entry, so browser Back returns
      // here. The owner has full access to their own session → real transcript.
      closePop();
      openDetail(sessionId);
    };
  });
}

// Grant/deny. On success (or a 409 from a concurrent actor) we REFETCH
// (`refreshInbox`) rather than filter the row out locally, which fixes three
// things the local-filter version got wrong:
//   - overflow: with >INBOX_LIMIT pending, local-filter would empty the visible
//     100 and hide the badge while more waited; a refetch pulls the next batch.
//   - stale row: a concurrent decide (or a second click) 409s — refetching drops
//     the already-decided row instead of stranding it with a scary error.
//   - latest-wins: refetch is the single source of truth, so overlapping triggers
//     can't resurrect a just-decided row.
// Buttons are disabled in flight so a double-click can't fire two /decide calls
// (the second of which would 409 and read as "failed" for an action that worked).
function decide(reqId: string, grant: boolean, acts?: HTMLElement | null) {
  var buttons = acts ? Array.prototype.slice.call(acts.querySelectorAll('button')) : [];
  var reenable = function () { buttons.forEach(function (b: HTMLButtonElement) { b.disabled = false; }); };
  var showErr = function (msg: string) { var e = document.getElementById('inboxErr'); if (e) e.textContent = msg; };
  buttons.forEach(function (b: HTMLButtonElement) { b.disabled = true; });
  postStatus('/api/access-requests/' + encodeURIComponent(reqId) + '/decide', { grant: grant })
    .then(function (r) {
      // 2xx OR 409 ("already decided" by a concurrent actor / a double-click) →
      // the row is settled server-side, so resync to server truth. No local
      // mutation, so the two surfaces can't drift; the drawer's Share tab (if open
      // on this session) refreshes too, mirroring its own inbox:refresh.
      if ((r.status >= 200 && r.status < 300) || r.status === 409) {
        document.dispatchEvent(new CustomEvent('sharing:refresh'));
        // On a 409 the row was decided by SOMEONE ELSE (or a double-click) —
        // possibly the opposite of what was just clicked. Confirm that after the
        // resync repaint (which rebuilds #inboxErr), so the owner isn't left
        // thinking their click took effect. A clean 2xx stays silent.
        // Best-effort: if this was the LAST pending request, refreshInbox tears the
        // whole badge/popover down (total→0), so #inboxErr is gone and showErr
        // no-ops — the note is dropped in that (single-request) case. The state is
        // still correct; a persistent notice would need a toast we don't have. The
        // drawer's Share tab (which persists) shows the same note reliably.
        return refreshInbox().then(function () {
          if (r.status === 409) showErr('This request was already decided by someone else.');
        });
      }
      // A genuine failure (400/403/500): the request did NOT change. Surface the
      // server's reason and re-enable the buttons so the owner can retry — never
      // swallow it into a silent no-op.
      reenable();
      showErr((r.body && r.body.error) || 'Could not update the request.');
    })
    .catch(function () {
      // Network failure: no server truth to resync to, so re-enable and surface it
      // rather than leaving the row dead.
      reenable();
      showErr('Network error.');
    });
}
